import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import { MemberPicker } from '../../components/ui';
import { useMemberDirectory } from '../../hooks';
import type { Member } from '../../types';

// The member field on a register form.
//
// It does two things a bare picker cannot. It fills the rest of the form from
// whoever is picked, so a clerk types the three facts the certificate adds
// rather than the eight the member record already holds. And it keeps the
// picked record itself on the form under a `_`-prefixed key, so `validate` and
// `warn` — which run outside React and cannot read the directory — can ask
// whether this person is already baptised, already married, and so on.
//
// The record is re-attached when an existing entry is opened for editing: the
// saved row carries only the member id, so without this the warnings would go
// quiet on exactly the screen where a mistake is being corrected.

export interface RegisterMemberFieldProps {
  form: any;
  setForm: (next: any) => void;
  /** Form key holding the member id, e.g. `memberId` or `groomMemberId`. */
  idKey: string;
  /** Form key holding the resolved record. Must start with `_`. */
  memberKey: string;
  label: ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  filter?: (member: Member) => boolean;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  isDarkMode?: boolean;
  /**
   * The fields this member should fill in. Called with null when the picker is
   * cleared, so the form can hand those fields back to the clerk rather than
   * leaving one person's details under another person's name.
   */
  autofill?: (member: Member | null, form: any) => Record<string, unknown>;
}

const RegisterMemberField = ({
  form, setForm, idKey, memberKey, label, hint, placeholder,
  filter, required, disabled, fullWidth, isDarkMode, autofill
}: RegisterMemberFieldProps) => {
  const { members } = useMemberDirectory();

  const id: string = form[idKey] || '';
  const attached: Member | null = form[memberKey] || null;

  // Resolve the id the form was built with — on edit it arrives alone.
  useEffect(() => {
    const found = members.find((m) => m.memberId === id) || null;
    if ((found?.memberId || '') !== (attached?.memberId || '')) {
      setForm({ ...form, [memberKey]: found });
    }
    // `form` is deliberately out of the dependency list: it changes on every
    // keystroke, and this only needs to run when the id or the directory does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, id, attached, memberKey]);

  const pick = (memberId: string, member: Member | null) => {
    setForm({
      ...form,
      [idKey]: memberId,
      [memberKey]: member,
      ...(autofill ? autofill(member, form) : {})
    });
  };

  return (
    <MemberPicker
      label={label}
      hint={hint}
      placeholder={placeholder}
      value={id}
      onChange={pick}
      filter={filter}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      isDarkMode={isDarkMode}
    />
  );
};

export default RegisterMemberField;
